import axios from "axios";
import { getContractorId } from "Common/Components/getRoleId";

const API_BASE_URL = "https://api.havirkesht.ir";
const WALLET_URL = `${API_BASE_URL}/contractor-settings/wallet`;
const WALLET_BALANCE_URL = `${API_BASE_URL}/contractor-settings/wallet-balance`;
const BANK_URL = `${API_BASE_URL}/farmers_payment/bank`;

export type BankPaymentStatus =
  | "prepared"
  | "processing"
  | "succeeded"
  | "failed"
  | "expired"
  | "cancelled"
  | "unknown";

export interface BankPayment {
  id: number | string;
  status: BankPaymentStatus;
  amount_rial?: number | null;
  recipient_name?: string | null;
  message?: string | null;
  expires_at?: string | null;
  payment_id?: number | null;
  bank_reference?: string | null;
  requires_reconciliation?: boolean | null;
  error_code?: string | null;
}

export interface WalletSettings {
  phone_number?: string | null;
  wallet_title?: string | null;
  bank_payments_enabled?: boolean;
}

export interface WalletBalance {
  balance_rial?: number | null;
  visibility?: string | null;
  reliable?: boolean;
  message?: string | null;
}

export interface NormalizedBankError {
  httpStatus?: number;
  code?: string;
  message: string;
  payment?: BankPayment | null;
  isNetworkError: boolean;
  isAuthError: boolean;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("app_atkey")}`,
});

export const BANK_ERROR_MESSAGES: Record<string, string> = {
  wallet_not_found:
    "برای کشاورز کیف پول ایجاد نشده است. لطفا ابتدا کیف پول کشاورز را ایجاد کنید.",
  wallet_identity_mismatch:
    "اطلاعات کیف پول با مشخصات کشاورز مغایرت دارد. لطفاً اطلاعات کشاورز و کیف پول را اصلاح کنید.",
  wallet_identity_changed:
    "اطلاعات کیف پول کشاورز تغییر کرده است. لطفاً اطلاعات کشاورز و کیف پول را بررسی و اصلاح کنید.",
  same_source_destination: "کیف پول پیمانکار و کشاورز نمی‌تواند یکسان باشد",
  insufficient_funds: "موجودی حساب کافی نیست",
  payment_requires_reconciliation:
    "پرداخت قبلی شما نیاز به تعیین وضعیت دارد. تا مشخص شدن نتیجه آن، پرداخت جدید ثبت نکنید.",
  bank_transfers_disabled: "پرداخت بانکی در حال حاضر در دسترس نیست",
  bank_not_configured: "پرداخت بانکی در حال حاضر در دسترس نیست",
  contractor_phone_missing:
    "شماره همراه پیمانکار ثبت نشده است. ابتدا از بخش داده‌های اولیه شماره همراه پیمانکار را ثبت کنید.",
  wallet_phone_not_set:
    "شماره همراه پیمانکار ثبت نشده است. ابتدا از بخش داده‌های اولیه شماره همراه پیمانکار را ثبت کنید.",
  settle_balance_changed:
    "اطلاعات پرداخت تغییر کرده است؛ لطفاً مبلغ و گیرنده را دوباره بررسی و تأیید کنید",
  wallet_changed:
    "اطلاعات پرداخت تغییر کرده است؛ لطفاً مبلغ و گیرنده را دوباره بررسی و تأیید کنید",
  payment_storage_error:
    "نتیجه پرداخت هنوز مشخص نیست. تا تعیین وضعیت، پرداخت را دوباره ثبت نکنید.",
  preview_expired:
    "اعتبار تأیید پرداخت به پایان رسیده است. لطفاً دوباره مبلغ و گیرنده را تأیید کنید.",
  payment_not_cancellable:
    "این پرداخت وارد مرحله ارسال شده است و قابل لغو نیست.",
  bank_payment_immutable:
    "پرداخت بانکی ثبت‌شده قابل ویرایش یا حذف نیست.",
};

export const BANK_STATUS_MESSAGES: Record<BankPaymentStatus, string> = {
  prepared: "در انتظار تأیید",
  processing: "پرداخت در حال بررسی است",
  succeeded: "پرداخت با موفقیت انجام شد",
  failed: "پرداخت ناموفق بود",
  expired:
    "اعتبار تأیید پرداخت به پایان رسیده است. لطفاً دوباره مبلغ و گیرنده را تأیید کنید.",
  cancelled: "پرداخت لغو شد",
  unknown:
    "نتیجه پرداخت هنوز مشخص نیست. تا تعیین وضعیت، پرداخت را دوباره ثبت نکنید.",
};

export const newIdempotencyKey = (): string => {
  const cryptoObj: any = window.crypto;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const formatRial = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  const numStr = String(value).replace(/,/g, "");
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const looksLikePayment = (data: any): boolean =>
  !!data && typeof data === "object" && typeof data.status === "string";

export const normalizeBankError = (error: any): NormalizedBankError => {
  const httpStatus: number | undefined = error?.response?.status;
  const data: any = error?.response?.data;

  if (!error?.response) {
    return {
      httpStatus,
      message: "ارتباط با سرور برقرار نشد. لطفاً دوباره تلاش کنید.",
      isNetworkError: true,
      isAuthError: false,
    };
  }

  if (httpStatus === 401) {
    return {
      httpStatus,
      code: "auth_required",
      message: "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.",
      isNetworkError: false,
      isAuthError: true,
    };
  }

  let code: string | undefined;
  let serverMessage: string | undefined;
  let payment: BankPayment | null = null;

  if (looksLikePayment(data)) {
    payment = data as BankPayment;
    code = data.error_code || undefined;
    serverMessage = data.message || undefined;
  }

  const detail = data?.detail;
  if (Array.isArray(detail)) {
    const first = detail.find((d: any) => d?.msg) as any;
    serverMessage = first?.msg
      ? "اطلاعات ارسالی معتبر نیست. لطفاً مقادیر فرم را بررسی کنید."
      : serverMessage;
    code = code || "validation_error";
  } else if (detail && typeof detail === "object") {
    code = code || detail.code || undefined;
    serverMessage = detail.message || serverMessage;
  } else if (typeof detail === "string") {
    if (BANK_ERROR_MESSAGES[detail]) {
      code = code || detail;
    } else {
      serverMessage = serverMessage || detail;
    }
  }

  if (!code && typeof data?.code === "string") code = data.code;

  const message =
    (code && BANK_ERROR_MESSAGES[code]) ||
    serverMessage ||
    (httpStatus === 403
      ? "شما اجازه انجام این عملیات را ندارید."
      : httpStatus === 404
        ? "اطلاعات درخواستی یافت نشد."
        : "خطایی در پرداخت بانکی رخ داد. لطفاً دوباره تلاش کنید.");

  return {
    httpStatus,
    code,
    message,
    payment,
    isNetworkError: false,
    isAuthError: false,
  };
};

export const getWalletSettings = async (): Promise<WalletSettings> => {
  const res = await axios.get(WALLET_URL, { headers: authHeaders() });
  return res?.data || {};
};

// مخصوص ادمین کل: فعال یا غیرفعال کردن پرداخت بانکی برای یک پیمانکار
export const setContractorPaymentMode = async (
  contractorId: number,
  bankPaymentsEnabled: boolean
): Promise<WalletSettings> => {
  const res = await axios.put(
    `${API_BASE_URL}/contractor-settings/payment-mode?contractor_id=${contractorId}`,
    { bank_payments_enabled: bankPaymentsEnabled },
    { headers: { ...authHeaders(), "Content-Type": "application/json" } }
  );
  return res?.data || {};
};

export const getWalletBalance = async (): Promise<WalletBalance> => {
  const res = await axios.get(WALLET_BALANCE_URL, { headers: authHeaders() });
  return res?.data || {};
};

export interface SettlementInfo {
  remaining_balance_rial?: string | null;
  amount_rial?: number | null;
  can_settle?: boolean;
}

export const getSettlementAmount = async (
  farmerId: number,
  cropYearId: number
): Promise<SettlementInfo> => {
  const res = await axios.get(
    `${API_BASE_URL}/farmers_payment/settlement?farmer_id=${farmerId}&crop_year_id=${cropYearId}`,
    { headers: authHeaders() }
  );
  return res?.data || {};
};

export interface BankPreviewBody {
  idempotency_key: string;
  farmer_id: number;
  crop_year_id: number;
  payment_type: number | string;
  price?: number;
  settle_balance: boolean;
}

export const previewBankPayment = async (
  body: BankPreviewBody
): Promise<BankPayment> => {
  const res = await axios.post(`${BANK_URL}/preview`, body, {
    headers: { ...authHeaders(), "Content-Type": "application/json" },
  });
  return res?.data;
};

export const confirmBankPayment = async (
  id: number | string
): Promise<BankPayment> => {
  const res = await axios.post(
    `${BANK_URL}/${id}/confirm`,
    { confirmed: true },
    { headers: { ...authHeaders(), "Content-Type": "application/json" } }
  );
  return res?.data;
};

export const cancelBankPayment = async (
  id: number | string
): Promise<BankPayment> => {
  const res = await axios.post(
    `${BANK_URL}/${id}/cancel`,
    undefined,
    { headers: authHeaders() }
  );
  return res?.data;
};

export const getBankPaymentStatus = async (
  id: number | string
): Promise<BankPayment> => {
  const res = await axios.get(`${BANK_URL}/${id}`, { headers: authHeaders() });
  return res?.data;
};

export const listBankPayments = async (params: {
  page?: number;
  size?: number;
  payment_status?: BankPaymentStatus;
}): Promise<{ items: BankPayment[]; total: number; size: number; pages: number }> => {
  const search = new URLSearchParams();
  search.set("page", String(params.page ?? 1));
  search.set("size", String(params.size ?? 50));
  if (params.payment_status) search.set("payment_status", params.payment_status);
  const res = await axios.get(`${BANK_URL}?${search.toString()}`, {
    headers: authHeaders(),
  });
  const data = res?.data;
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: data?.total ?? 0,
    size: data?.size ?? 0,
    pages: data?.pages ?? 0,
  };
};

export interface PendingBankPayment {
  id: number | string;
  idempotency_key: string;
  farmer_id: number;
  farmer_label?: string;
  crop_year_id: number;
  payment_type: number | string;
  price?: number;
  settle_balance: boolean;
  amount_rial?: number | null;
  recipient_name?: string | null;
  status: BankPaymentStatus;
}

const pendingStorageKey = (): string => {
  const contractorId = getContractorId(localStorage.getItem("app_atkey"));
  return `bank_payment_pending_${contractorId ?? "unknown"}`;
};

export const savePendingBankPayment = (pending: PendingBankPayment): void => {
  try {
    localStorage.setItem(pendingStorageKey(), JSON.stringify(pending));
  } catch (error) {
    console.error("خطا در ذخیره پرداخت در جریان:", error);
  }
};

export const loadPendingBankPayment = (): PendingBankPayment | null => {
  try {
    const raw = localStorage.getItem(pendingStorageKey());
    return raw ? (JSON.parse(raw) as PendingBankPayment) : null;
  } catch (error) {
    return null;
  }
};

export const clearPendingBankPayment = (): void => {
  try {
    localStorage.removeItem(pendingStorageKey());
  } catch (error) {
    console.error("خطا در پاک کردن پرداخت در جریان:", error);
  }
};
